/**
 * AccountPicker — the A2.3 company field: a type-ahead over GET
 * /v1/sales/accounts with an inline "create" row.
 *
 * Contract: the input's text IS the deal's company (free text stays legal —
 * P1-2 kept company as the fallback for unlinked deals), and picking a row
 * also sets accountId. Any edit after a pick clears accountId: the link is
 * to a NAMED account, and a changed name is no longer that account.
 *
 * The create row goes through POST /v1/sales/accounts; when the name is
 * already taken (the 0116 per-tenant unique → 409 with existingAccountId)
 * the picker links the existing account instead of surfacing a dead end.
 *
 * Visual (docs/design/stier/ADOPTION.md): the input keeps the control
 * radius (§8.4 REFUSED list — text inputs never capsule); the results panel
 * is a .k-card (§3.4 contract) under the input; rows are plain hover rows;
 * the create row is the quiet action (§5 EmptyState precedent: .k-btn-quiet,
 * never a filled button inside a list). No new tokens.
 *
 * §25 L-1: the CONTACT surfaces mount the same picker with `allowCreate`
 * false — an explicit pick only, no create row (P1-1: no accounts minted
 * from contacts), so a company nobody has a deal with shows "No matching
 * account" and nothing to pick — and `clearable`, which turns the linked
 * mark into an "Unlink account" control (L-2: a link can be cleared). The
 * deal surfaces pass neither and render exactly as before.
 *
 * §35 item 8: the combobox itself (open/close, debounce, roles, click-away,
 * the "Searching…" line, the two empty sentences) moved to EntityPicker, the
 * core the contact and deal pickers share. What stays here is everything
 * account-flavoured: the {accountId, company} contract, the create row and
 * its 409 fallback, the link marks and the unlink control. Behaviour and
 * markup are unchanged; the tests did not move.
 */

import { useState } from 'react'
import { Building2, Plus, Search, X } from 'lucide-react'
import { fetchAccounts, createAccount, conflictingAccountId } from '../../api'
import type { SalesAccountRow } from '../../types'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { EntityPicker } from './EntityPicker'

export interface AccountPick {
  /** '' when the company text is free text (no linked account). */
  accountId: string
  company: string
}

export function AccountPicker({ value, onChange, className, placeholder, autoFocus, id, allowCreate = true, clearable = false, onBlur }: {
  value: AccountPick
  onChange: (next: AccountPick) => void
  className: string
  placeholder?: string
  autoFocus?: boolean
  id?: string
  /** false = pick only: no create row, ever (the contact surfaces, P1-1). */
  allowCreate?: boolean
  /** true = a linked pick shows an "Unlink account" control that clears the
   *  id and keeps the text (the contact surfaces, L-2). */
  clearable?: boolean
  /** Fires when the input loses focus — the L-4 suggestion hook's trigger. */
  onBlur?: () => void
}) {
  const [creating, setCreating] = useState(false)
  const q = value.company
  const trimmed = q.trim()

  function pick(r: { id: string; name: string }) {
    onChange({ accountId: r.id, company: r.name })
  }

  async function create(close: () => void) {
    if (!trimmed || creating) return
    setCreating(true)
    try {
      const a = await createAccount({ name: trimmed })
      toast.success(`Account "${a.name}" created`)
      pick(a)
      close()
    } catch (e) {
      const existing = conflictingAccountId(e)
      if (existing) { pick({ id: existing, name: trimmed }); close(); return }
      toast.error(apiErrorMessage(e, 'Failed to create account'))
    } finally {
      setCreating(false)
    }
  }

  const adornment = (
    <>
      {/* §25 (the L-9 recon's finding): at rest an UNLINKED picker holding
          text was indistinguishable from the old free-text input — nothing
          said it could pick. The contact surfaces (clearable) now carry a
          faint search mark whenever text is present and no link is; an empty
          field carries nothing, so the New Contact drawer's pin holds. */}
      {clearable && !value.accountId && trimmed !== '' && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" title="Not linked to an account — type to search, or pick a suggestion">
          <Search size={13} />
        </span>
      )}
      {value.accountId && (clearable ? (
        <button type="button" onClick={() => onChange({ accountId: '', company: value.company })}
          aria-label="Unlink account" title="Unlink account"
          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-ink-faint hover:text-ink-primary transition-colors">
          <Building2 size={13} /><X size={11} />
        </button>
      ) : (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" title="Linked account">
          <Building2 size={13} />
        </span>
      ))}
    </>
  )

  return (
    <EntityPicker<SalesAccountRow>
      id={id} className={className} placeholder={placeholder ?? 'Search accounts…'} autoFocus={autoFocus} onBlur={onBlur}
      text={q}
      onTextChange={t => onChange({ accountId: '', company: t })}
      // The server matches on name_normalised, so case and edge whitespace
      // never matter.
      search={s => fetchAccounts(s, 8)}
      rowKey={r => r.id}
      selectedKey={value.accountId || undefined}
      renderRow={r => (
        <>
          <span className="text-13 font-medium text-ink-primary truncate">{r.name}</span>
          <span className="text-11 text-ink-faint whitespace-nowrap">
            {r.openDealCount} open · {r.contactCount} contact{r.contactCount === 1 ? '' : 's'}
          </span>
        </>
      )}
      onPick={pick}
      emptyPrompt="Type to search accounts"
      noMatch="No matching account"
      adornment={adornment}
      footer={({ rows, trimmed: t, close }) => {
        const exact = rows.find(r => r.name.toLowerCase() === t.toLowerCase())
        if (!allowCreate || t === '' || exact) return null
        return (
          <button type="button" onClick={() => void create(close)} disabled={creating}
            className="k-btn-quiet w-full flex items-center gap-2 px-4 py-2.5 border-t border-edge-row rounded-none disabled:opacity-50">
            <Plus size={12} /> {creating ? 'Creating…' : `Create account "${t}"`}
          </button>
        )
      }}
    />
  )
}
